
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { BarChart4, Calendar, Clock, Coins, Headphones, HelpCircle, Phone, User } from "lucide-react";

interface ConversationDataCardProps {
  conversationData: Record<string, any> | null;
}

const ConversationDataCard = ({ conversationData }: ConversationDataCardProps) => {
  if (!conversationData) {
    return null;
  }

  // Group fields into categories for better organization
  const groupFields = () => {
    const groups = {
      leadInfo: ['lead_first_name', 'external_number', 'dealership_name'],
      callDetails: ['call_start_time', 'call_duration_secs', 'call_cost_tokens', 'agent_name'],
      outcomes: ['outcome', 'booking_made', 'phone_number_updated', 'termination_reason'],
      ids: ['id', 'call_sid', 'conversation_id'],
      timestamps: ['created_at'],
      // These will be shown in separate tabs
      excluded: ['transcript_summary', 'reactivated_rationale', 'audio_url', 'audio_fetched', 'metadata_json']
    };

    // Create a reverse mapping for categorization
    const fieldCategories = {};
    Object.entries(groups).forEach(([category, fields]) => {
      fields.forEach(field => {
        fieldCategories[field] = category;
      });
    });

    // Organize data by categories
    const result = {};
    Object.entries(conversationData).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;
      
      const category = fieldCategories[key] || 'other';
      if (category !== 'excluded') {
        if (!result[category]) {
          result[category] = {};
        }
        result[category][key] = value;
      }
    });

    return result;
  };

  // Format field values according to their type and meaning
  const formatValue = (key: string, value: any) => {
    if (value === null || value === undefined) return '—';

    // Format dates
    if (key === 'created_at' || key === 'call_start_time' || key.includes('time') && typeof value === 'string') {
      try {
        // Handle unix timestamps (check if it's a number or numeric string)
        if (/^\d+$/.test(value.toString()) && value.toString().length >= 10) {
          const timestamp = parseInt(value.toString()) * (value.toString().length === 10 ? 1000 : 1);
          return new Date(timestamp).toLocaleString();
        }
        // Handle ISO date strings
        return new Date(value).toLocaleString();
      } catch (e) {
        return value;
      }
    }

    // Format boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    // Format outcome
    if (key === 'outcome') {
      const outcomeColorMap = {
        'reactivated': 'success',
        'callback_requested': 'success',
        'no_response': 'destructive',
        'not_interested': 'warning',
        'wrong_number': 'destructive',
        'declined': 'warning',
        'default': 'secondary'
      };
      
      const variant = outcomeColorMap[value.toLowerCase()] || outcomeColorMap.default;
      
      return (
        <Badge variant={variant as any}>{value}</Badge>
      );
    }

    // Format JSON data
    if (typeof value === 'object') {
      return (
        <pre className="whitespace-pre-wrap text-xs bg-gray-50 p-2 rounded border max-h-32 overflow-y-auto">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    // Format phone numbers
    if (key === 'external_number' || key.includes('phone')) {
      return <span className="font-mono">{value}</span>;
    }

    // Format IDs (truncate and add monospace)
    if (key === 'id' || key === 'call_sid' || key === 'conversation_id') {
      return <span className="font-mono text-xs">{value}</span>;
    }

    return value;
  };

  // Get icon for category
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'leadInfo':
        return <User className="h-4 w-4" />;
      case 'callDetails':
        return <Phone className="h-4 w-4" />;
      case 'outcomes':
        return <BarChart4 className="h-4 w-4" />;
      case 'ids':
        return <HelpCircle className="h-4 w-4" />;
      case 'timestamps':
        return <Calendar className="h-4 w-4" />;
      default:
        return null;
    }
  };

  // Get human-readable category name
  const getCategoryName = (category: string) => {
    const names = {
      'leadInfo': 'Lead Information',
      'callDetails': 'Call Details',
      'outcomes': 'Call Outcomes',
      'ids': 'Identifiers',
      'timestamps': 'Timestamps',
      'other': 'Additional Information'
    };
    return names[category] || 'Other';
  };

  const fieldLabels = {
    'id': 'Record ID',
    'created_at': 'Created',
    'dealership_name': 'Dealership',
    'external_number': 'Lead Phone Number',
    'agent_name': 'AI Agent Name',
    'lead_first_name': 'Lead Name',
    'call_start_time': 'Call Started',
    'call_duration_secs': 'Call Duration',
    'call_cost_tokens': 'Token Cost',
    'outcome': 'Call Outcome',
    'booking_made': 'Booking Made',
    'phone_number_updated': 'Phone Updated',
    'termination_reason': 'Call Ended Because',
    'call_sid': 'Call SID',
    'conversation_id': 'Conversation ID'
  };

  const groupedData = groupFields();

  return (
    <div className="space-y-6">
      {Object.entries(groupedData).map(([category, fields]) => (
        <Card key={category} className="overflow-hidden">
          <div className="bg-accent/50 px-6 py-3 flex items-center">
            {getCategoryIcon(category)}
            <h3 className="text-sm font-medium ml-2">{getCategoryName(category)}</h3>
          </div>
          <CardContent className="p-0">
            <Table>
              <TableBody>
                {Object.entries(fields as Record<string, any>).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell className="font-medium text-muted-foreground w-1/3 py-3 pl-6">
                      {fieldLabels[key] || key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </TableCell>
                    <TableCell className="py-3 pr-6">
                      {formatValue(key, value)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ConversationDataCard;
